import {
  ChainCardType,
  ChainMaker,
  ChainPair,
  ValueChain,
} from "../../types/valueChain";
import type { DetailSection } from "../Detail/DetailSections";
import ChainNeighbours, { chainNeighbours } from "./ChainNeighbours";
import KindFacilities, { kindFacilities } from "./KindFacilities";
import KindMakers from "./KindMakers";
import { MakeRoot, cardOfCategory } from "./kindChain";

// A count for the strip, where the section holds anything to count
const countOf = (count: number): number | undefined =>
  count > 0 ? count : undefined;

/**
 * The makers of a kind or a class: as the card of the chain the page shows
 * has them, where the page shows one for it, else as its own read has them.
 * On the page, "proven" means proven in that chain.
 */
export const chainMakers = (
  own: ValueChain | null | undefined,
  page: ValueChain | null | undefined,
  categoryId: string,
): ChainMaker[] =>
  cardOfCategory(page, categoryId)?.makers ?? own?.root.makers ?? [];

/**
 * The stated pairs with the products of a kind or a class as parts, from the
 * same chain as its makers: the chain the page shows, where it has a card for
 * it, else its own read.
 */
export const chainPairs = (
  own: ValueChain | null | undefined,
  page: ValueChain | null | undefined,
  categoryId: string,
): ChainPair[] => {
  const onPage = cardOfCategory(page, categoryId);
  const chain = onPage ? page : own;
  const card = onPage ?? own?.root;
  if (!chain || !card) {
    return [];
  }
  return chain.edges
    .filter((edge) => edge.part === card.id)
    .flatMap((edge) => edge.stated_pairs);
};

/**
 * The facilities section of a kind or a class: the production sites of the
 * makers in its own chain read, each once. Left out with none on record.
 */
export const facilitiesSection = (
  own: ValueChain | null | undefined,
): DetailSection => {
  const facilities = kindFacilities(own?.root.makers ?? []);
  return {
    id: "facilities",
    label: "Facilities",
    count: countOf(facilities.length),
    content: facilities.length > 0 && (
      <KindFacilities facilities={facilities} />
    ),
  };
};

/**
 * The sections a kind or a class adds from the value chain: its makers, the
 * kinds it is built into and the kinds of part stated inside it. A section
 * with nothing on record is left out.
 */
export const chainSections = (
  type: ChainCardType,
  own: ValueChain | null | undefined,
  page: ValueChain | null | undefined,
  categoryId: string,
  onMakeRoot: MakeRoot,
): DetailSection[] => {
  const makers = chainMakers(own, page, categoryId);
  const hosts = own ? chainNeighbours(own, "hosts") : [];
  const parts = own ? chainNeighbours(own, "parts") : [];
  // The value chain opens on the root by its id in its own read
  const root = { type, id: own?.root.id ?? categoryId };
  return [
    {
      id: "makers",
      label: "Makers",
      count: countOf(makers.length),
      content: makers.length > 0 && (
        <KindMakers
          makers={makers}
          categoryId={categoryId}
          type={type}
          pairs={chainPairs(own, page, categoryId)}
        />
      ),
    },
    {
      id: "built-into",
      label: "Built into",
      count: countOf(hosts.length),
      content: hosts.length > 0 && (
        <ChainNeighbours
          rows={hosts}
          side="hosts"
          label="Built into"
          root={root}
          onMakeRoot={onMakeRoot}
        />
      ),
    },
    {
      id: "parts",
      label: "Parts",
      count: countOf(parts.length),
      content: parts.length > 0 && (
        <ChainNeighbours
          rows={parts}
          side="parts"
          label="Parts"
          root={root}
          onMakeRoot={onMakeRoot}
        />
      ),
    },
  ];
};
